import {numberSpaceFormatter} from '@rocket-ui/tools';
import type {Bank} from '@rocket-ui/lk-api';
import {PAYMENT_ACCOUNT_INVALID_MESSAGE, PaymentSubStep} from './paymentFlow';
import {RegistrationPaymentMethod} from './paymentMethod';
import {RegistrationWorkStatus} from './types';

export type SelectedBank = Pick<Bank, 'id' | 'bik' | 'shortName' | 'iconUrl'>;

export type PaymentDetailsValues = {
	phone: string;
	card: string;
	bik: string;
	account: string;
	selectedBank: SelectedBank | null;
	isPrimary: boolean;
	accountError: string | null;
};

export const EMPTY_PAYMENT_DETAILS: PaymentDetailsValues = {
	phone: '',
	card: '',
	bik: '',
	account: '',
	selectedBank: null,
	isPrimary: false,
	accountError: null,
};

const PHONE_DIGITS_LENGTH = 10;
const CARD_DIGITS_LENGTH = 16;
const BIK_DIGITS_LENGTH = 9;
const ACCOUNT_DIGITS_LENGTH = 20;
/** Балансовый счёт ИП (расчёты индивидуальных предпринимателей) */
export const IE_ACCOUNT_PREFIX = '40802';
/** Маска отображения как в плейсхолдере: 40802 810 40 58316 76842 */
const ACCOUNT_DISPLAY_PATTERN = '##### ### ## ##### #####';

export const requiresPaymentDetailsStep = (method: RegistrationPaymentMethod): boolean => {
	return method !== 'executor_account/swift';
};

export const extractPhoneDigits = (value: string): string => {
	const digits = value.replace(/\D/g, '');

	if (digits.startsWith('7') || digits.startsWith('8')) {
		return digits.slice(1, 1 + PHONE_DIGITS_LENGTH);
	}

	return digits.slice(0, PHONE_DIGITS_LENGTH);
};

export const formatPhoneDisplay = (digits: string): string => {
	const normalized = extractPhoneDigits(digits);

	if (!normalized) {
		return '';
	}

	const a = normalized.slice(0, 3);
	const b = normalized.slice(3, 6);
	const c = normalized.slice(6, 8);
	const d = normalized.slice(8, 10);

	if (normalized.length <= 3) {
		return `(${a}`;
	}

	if (normalized.length <= 6) {
		return `(${a}) ${b}`;
	}

	if (normalized.length <= 8) {
		return `(${a}) ${b}-${c}`;
	}

	return `(${a}) ${b}-${c}-${d}`;
};

export const formatPhoneForApi = (digits: string): string => {
	const normalized = extractPhoneDigits(digits);

	return normalized ? `+7${normalized}` : '';
};

export const formatPhoneForReview = (digits: string): string => {
	const normalized = extractPhoneDigits(digits);

	if (!normalized) {
		return '';
	}

	return `+ 7 (${normalized.slice(0, 3)}) ${normalized.slice(3, 6)}-${normalized.slice(6, 8)}-${normalized.slice(8, 10)}`;
};

export const extractCardDigits = (value: string): string => {
	return value.replace(/\D/g, '').slice(0, CARD_DIGITS_LENGTH);
};

export const formatCardDisplay = (digits: string): string => {
	const normalized = extractCardDigits(digits);

	if (!normalized) {
		return '';
	}

	return normalized.match(/.{1,4}/g)?.join(' ') ?? normalized;
};

export const extractBikDigits = (value: string): string => {
	return value.replace(/\D/g, '').slice(0, BIK_DIGITS_LENGTH);
};

export const formatBikDisplay = (digits: string): string => {
	const normalized = extractBikDigits(digits);

	if (!normalized) {
		return '';
	}

	return numberSpaceFormatter(normalized).toBik();
};

export const extractAccountDigits = (value: string): string => {
	return value.replace(/\D/g, '').slice(0, ACCOUNT_DIGITS_LENGTH);
};

export const formatAccountDisplay = (digits: string): string => {
	const normalized = extractAccountDigits(digits);

	if (!normalized) {
		return '';
	}

	let i = 0;
	const formatted = ACCOUNT_DISPLAY_PATTERN.replace(/\S/g, () => normalized[i++] || '');

	// Без хвостовых пробелов от незаполненных слотов маски — иначе Backspace «залипает».
	let lastDigitIndex = -1;

	for (let index = 0; index < formatted.length; index++) {
		if (/\d/.test(formatted[index]!)) {
			lastDigitIndex = index;
		}
	}

	return lastDigitIndex < 0 ? '' : formatted.slice(0, lastDigitIndex + 1);
};

export type AccountNumberValidationOptions = {
	workStatus?: RegistrationWorkStatus | null;
};

export const requiresIeAccountMask = (workStatus: RegistrationWorkStatus | null | undefined): boolean => {
	return workStatus === 'individual_entrepreneur';
};

export const isAccountNumberValid = (
	digits: string,
	options: AccountNumberValidationOptions = {},
): boolean => {
	const account = extractAccountDigits(digits);

	if (account.length !== ACCOUNT_DIGITS_LENGTH) {
		return false;
	}

	if (requiresIeAccountMask(options.workStatus) && !account.startsWith(IE_ACCOUNT_PREFIX)) {
		return false;
	}

	return true;
};

/** Ошибка под полем: только когда введены все 20 цифр, но маска не сошлась. */
export const getAccountNumberError = (
	digits: string,
	options: AccountNumberValidationOptions = {},
): string | null => {
	const account = extractAccountDigits(digits);

	if (account.length !== ACCOUNT_DIGITS_LENGTH) {
		return null;
	}

	if (isAccountNumberValid(account, options)) {
		return null;
	}

	return PAYMENT_ACCOUNT_INVALID_MESSAGE;
};

export const isSbpPhoneValid = (digits: string): boolean => {
	return extractPhoneDigits(digits).length === PHONE_DIGITS_LENGTH;
};

export const isCardNumberValid = (digits: string): boolean => {
	return extractCardDigits(digits).length === CARD_DIGITS_LENGTH;
};

export const isBikValid = (digits: string): boolean => {
	return extractBikDigits(digits).length === BIK_DIGITS_LENGTH;
};

export const isPaymentSubStepValid = (
	method: RegistrationPaymentMethod | null,
	subStep: PaymentSubStep,
	values: PaymentDetailsValues,
	options: AccountNumberValidationOptions = {},
): boolean => {
	if (!method) {
		return false;
	}

	switch (subStep) {
		case 'sbp_phone':
			return isSbpPhoneValid(values.phone);
		case 'sbp_bank':
			return values.selectedBank !== null;
		case 'sbp_review':
		case 'account_review':
		case 'card_review':
			return true;
		case 'card_number':
			return isCardNumberValid(values.card);
		case 'account_bik':
			return isBikValid(values.bik) && values.selectedBank !== null;
		case 'account_number':
			return (
				isAccountNumberValid(values.account, {workStatus: options.workStatus}) && !values.accountError
			);
		default:
			return false;
	}
};

/** @deprecated use isPaymentSubStepValid */
export const isPaymentDetailsValid = (
	method: RegistrationPaymentMethod,
	values: PaymentDetailsValues,
): boolean => {
	switch (method) {
		case 'sbp_account':
			return isSbpPhoneValid(values.phone);
		case 'bank_card':
			return isCardNumberValid(values.card);
		case 'executor_account':
			return isBikValid(values.bik);
		default:
			return true;
	}
};

export const bankFromApi = (bank: Bank): SelectedBank => ({
	id: bank.id,
	bik: bank.bik ?? null,
	shortName: bank.shortName ?? null,
	iconUrl: bank.iconUrl ?? null,
});
